import { FC, useState } from "react";
import BarChart from "../components/BarChart/BarChart";
import EmptyState from "../components/EmptyState/EmptyState";
import useTrackingAggregate from "../hooks/useTrackingAggregate";
import { type Period } from "../utils/period";
import { EventNames } from "../utils/eventNames";
import { makeTablistKeyHandler } from "../utils/tablistKeyboard";

interface Props {
	period: Period;
	availableEvents: Set<string>;
}

type DrillLevel = "orgTeamLevelOne" | "orgTeamLevelTwo" | "storeId";

const AdoptionTab: FC<Props> = ({ period, availableEvents }) => {
	const [drill, setDrill] = useState<DrillLevel>("orgTeamLevelOne");
	const event = EventNames.SESSION_START;
	const enabled = availableEvents.has(event);

	const region = useTrackingAggregate(
		{
			event,
			from: period.from,
			to: period.to,
			groupBy: drill
		},
		{ enabled }
	);

	const os = useTrackingAggregate(
		{ event, from: period.from, to: period.to, groupBy: "os" },
		{ enabled }
	);
	const appVersion = useTrackingAggregate(
		{
			event,
			from: period.from,
			to: period.to,
			groupBy: "appVersion"
		},
		{ enabled }
	);

	if (!enabled) {
		return (
			<EmptyState
				title="No data yet"
				description={`The ${event} event isn't being emitted yet.`}
				tone="warn"
			/>
		);
	}

	return (
		<div className="space-y-8">
			<div>
				<div className="flex items-center justify-between mb-3">
					<h3 className="font-heading text-base font-medium text-foreground">
						Active users by location
					</h3>
					<div
						className="inline-flex bg-white border border-border p-1 rounded-lg shadow-xs"
						role="tablist"
						aria-label="Drill-down level"
						onKeyDown={makeTablistKeyHandler<DrillLevel>(
							["orgTeamLevelOne", "orgTeamLevelTwo", "storeId"],
							drill,
							setDrill
						)}
					>
						{(
							[
								{ key: "orgTeamLevelOne", label: "Region" },
								{ key: "orgTeamLevelTwo", label: "Area" },
								{ key: "storeId", label: "Store" }
							] as { key: DrillLevel; label: string }[]
						).map((g) => (
							<button
								key={g.key}
								type="button"
								role="tab"
								aria-selected={drill === g.key}
								tabIndex={drill === g.key ? 0 : -1}
								onClick={() => setDrill(g.key)}
								className={`px-3 py-1.5 text-sm rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
									drill === g.key
										? "bg-primary text-white"
										: "text-zinc-600 hover:text-foreground"
								}`}
							>
								{g.label}
							</button>
						))}
					</div>
				</div>
				<div className="rounded-xl border border-border bg-white shadow-sm p-4">
					{region.isLoading ? (
						<div className="h-32 animate-pulse bg-zinc-100 rounded" />
					) : (
						<BarChart
							ariaLabel={`Active users grouped by ${drill}`}
							data={(region.data?.buckets ?? []).map((b) => ({
								label: b.bucket,
								value: b.count
							}))}
							valueLabel="sessions"
						/>
					)}
					{region.data?.data_source === "rollup" ? (
						<p className="text-xs text-muted-foreground mt-3">Updated hourly</p>
					) : null}
				</div>
			</div>

			<div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
				<div className="rounded-xl border border-border bg-white shadow-sm p-4">
					<h3 className="font-heading text-base font-medium mb-3">
						Operating system split
					</h3>
					{os.isLoading ? (
						<div className="h-32 animate-pulse bg-zinc-100 rounded" />
					) : (
						<BarChart
							ariaLabel="Sessions by OS"
							data={(os.data?.buckets ?? []).map((b) => ({
								label: b.bucket,
								value: b.count
							}))}
							valueLabel="sessions"
						/>
					)}
				</div>
				<div className="rounded-xl border border-border bg-white shadow-sm p-4">
					<h3 className="font-heading text-base font-medium mb-3">
						App version drift
					</h3>
					{appVersion.isLoading ? (
						<div className="h-32 animate-pulse bg-zinc-100 rounded" />
					) : (
						<BarChart
							ariaLabel="Sessions by app version"
							data={(appVersion.data?.buckets ?? []).map((b) => ({
								label: b.bucket,
								value: b.count
							}))}
							valueLabel="sessions"
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default AdoptionTab;
